import jsPDF from 'jspdf';

/**
 * Genera la Ficha de Identificación del Distribuidor en formato PDF.
 * @param {Object} params
 * @param {Object} params.distribuidor - Información del cliente / distribuidor
 * @param {Object} params.estadisticas - Estadísticas de órdenes de spr_AdminPanel_Distribuidor_GetEstadisticasOrdenes
 */
export const exportFichaDistribuidorPdf = ({ distribuidor = {}, estadisticas = {} }) => {
    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'letter'
    });

    const pageWidth = 215.9; // Letter width
    const pageHeight = 279.4; // Letter height
    const margin = 14;
    const contentWidth = pageWidth - (margin * 2);

    // Colores corporativos
    const colorPrimary = [44, 123, 229];      // #2c7be5
    const colorDark = [18, 38, 63];            // #12263f
    const colorGold = [217, 119, 6];           // Gold/Amber
    const colorMuted = [100, 116, 139];        // Gray
    const colorLightBg = [248, 250, 252];      // Light bg
    const colorBorder = [226, 232, 240];

    // Helper: Header decorativo
    doc.setFillColor(colorDark[0], colorDark[1], colorDark[2]);
    doc.rect(0, 0, pageWidth, 28, 'F');

    // Banda dorada superior
    doc.setFillColor(colorGold[0], colorGold[1], colorGold[2]);
    doc.rect(0, 28, pageWidth, 2.5, 'F');

    // Título en Header
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('AKASIA SOTWARE DEVELOPMENT S.A. DE C.V.', margin, 12);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(203, 213, 225);
    doc.text('FICHA DE IDENTIFICACIÓN DE DISTRIBUIDOR', margin, 18);

    // Fecha en Header (derecha)
    const hoy = new Date();
    const fechaEmision = hoy.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
    doc.setFontSize(8.5);
    doc.text(`Emisión: ${fechaEmision}`, pageWidth - margin, 18, { align: 'right' });

    let y = 37;

    // Caja de Identidad del Distribuidor
    doc.setFillColor(colorLightBg[0], colorLightBg[1], colorLightBg[2]);
    doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
    doc.roundedRect(margin, y, contentWidth, 32, 2, 2, 'FD');

    // ID y Badge
    const idCliente = distribuidor.idClienteAkasia || 'N/A';
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(colorPrimary[0], colorPrimary[1], colorPrimary[2]);
    doc.text(`ID DISTRIBUIDOR #${idCliente}`, margin + 6, y + 9);

    // Badge status real
    const statusText = `${distribuidor.vDescripcionTipo || 'DISTRIBUIDOR'}${distribuidor.bIsActive === false ? ' - INACTIVO' : ''}`;
    doc.setFillColor(distribuidor.bIsActive === false ? 239 : 34, distribuidor.bIsActive === false ? 68 : 197, distribuidor.bIsActive === false ? 68 : 94);
    doc.roundedRect(pageWidth - margin - 52, y + 4.5, 46, 6.5, 1.5, 1.5, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.text(statusText, pageWidth - margin - 29, y + 9, { align: 'center' });

    // Nombre del distribuidor
    const nombreCompleto = distribuidor.vNombreDistribuidor ||
        `${distribuidor.vNombre || ''} ${distribuidor.vApellidos || ''}`.trim() || 'Sin Nombre';
    doc.setTextColor(colorDark[0], colorDark[1], colorDark[2]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(nombreCompleto.toUpperCase(), margin + 6, y + 17);

    // Razón Social y Agente
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(colorMuted[0], colorMuted[1], colorMuted[2]);
    const razonSocial = distribuidor.vRazonSocial || 'Sin Razón Social asignada';
    doc.text(`Razón Social: ${razonSocial}`, margin + 6, y + 23);

    const agente = distribuidor.vAgente || distribuidor.vCreatedUser || 'N/A';
    doc.text(`Agente Asignado: ${agente}`, margin + 6, y + 28);

    const fechaAlta = distribuidor.reg_desde || distribuidor.joinDate || 'N/A';
    doc.text(`Fecha Registro: ${fechaAlta}`, pageWidth - margin - 6, y + 28, { align: 'right' });

    y += 38;

    // Sección 1: Datos de Contacto y Fiscales (2 columnas)
    const colWidth = (contentWidth - 6) / 2;

    // Columna Izquierda: Contacto
    doc.setFillColor(colorLightBg[0], colorLightBg[1], colorLightBg[2]);
    doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
    doc.roundedRect(margin, y, colWidth, 48, 2, 2, 'FD');

    // Header columna contacto
    doc.setFillColor(colorPrimary[0], colorPrimary[1], colorPrimary[2]);
    doc.roundedRect(margin, y, colWidth, 7, 2, 2, 'F');
    doc.rect(margin, y + 5, colWidth, 2, 'F'); // flatten bottom
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text('INFORMACIÓN DE CONTACTO', margin + 4, y + 5);

    let contactY = y + 12;
    const addField = (label, value, xPos, currentY) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(colorDark[0], colorDark[1], colorDark[2]);
        doc.text(`${label}:`, xPos, currentY);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(colorMuted[0], colorMuted[1], colorMuted[2]);
        const valStr = String(value || 'N/A');
        const truncated = valStr.length > 34 ? `${valStr.substring(0, 32)}...` : valStr;
        doc.text(truncated, xPos + 22, currentY);
    };

    addField('Email', distribuidor.vEmail, margin + 4, contactY);
    addField('Teléfono', distribuidor.vTelefonoPersonal || distribuidor.vTelefonoTrabajo, margin + 4, contactY + 6);
    addField('País', distribuidor.vPais, margin + 4, contactY + 12);
    addField('Estado', distribuidor.vEstadoDepProv, margin + 4, contactY + 18);
    addField('C.P.', distribuidor.vCp, margin + 4, contactY + 24);
    addField('Licencias', distribuidor.iNL !== undefined ? distribuidor.iNL : 'N/A', margin + 4, contactY + 30);

    // Columna Derecha: Fiscales
    const col2X = margin + colWidth + 6;
    doc.setFillColor(colorLightBg[0], colorLightBg[1], colorLightBg[2]);
    doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
    doc.roundedRect(col2X, y, colWidth, 48, 2, 2, 'FD');

    // Header columna fiscal
    doc.setFillColor(colorGold[0], colorGold[1], colorGold[2]);
    doc.roundedRect(col2X, y, colWidth, 7, 2, 2, 'F');
    doc.rect(col2X, y + 5, colWidth, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text('DATOS FISCALES', col2X + 4, y + 5);

    let fiscalY = y + 12;
    addField('RFC', distribuidor.vRfc, col2X + 4, fiscalY);
    addField('Reg. Trib.', distribuidor.vNumRegTrib, col2X + 4, fiscalY + 6);
    addField('Régimen', distribuidor.vDescripcionRegimenFiscal || distribuidor.vCodigoRegimen, col2X + 4, fiscalY + 12);
    addField('Uso CFDI', distribuidor.vDescripcionUsoCFDI || distribuidor.vCodigoUso, col2X + 4, fiscalY + 18);
    addField('País Fact.', distribuidor.vPaisFacturacion, col2X + 4, fiscalY + 24);
    addField('Dirección', distribuidor.vDireccionFiscal, col2X + 4, fiscalY + 30);

    y += 54;

    // Sección 2: Desempeño Comercial y Órdenes Pagadas
    doc.setFillColor(colorDark[0], colorDark[1], colorDark[2]);
    doc.roundedRect(margin, y, contentWidth, 7, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('DESEMPEÑO COMERCIAL - ÓRDENES PAGADAS', margin + 5, y + 5);

    y += 11;

    // 3 Cards Grandes de Órdenes (Mes Actual, Pasado, Antepasado)
    const cardOrderWidth = (contentWidth - 8) / 3;
    const resumen = estadisticas.resumen || {};
    const historico = estadisticas.historico || [];

    const NOMBRES_MESES = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    const getNombreMes = (num) => (num >= 1 && num <= 12 ? NOMBRES_MESES[num - 1] : '');

    const mesActualInfo = historico.find((h) => h.iOrden === 6);
    const mesAnteriorInfo = historico.find((h) => h.iOrden === 5);
    const mesAntepasadoInfo = historico.find((h) => h.iOrden === 4);

    const etiquetaMesActual = mesActualInfo ? `${getNombreMes(mesActualInfo.iMes)} ${mesActualInfo.iAnio}` : 'Mes Actual';
    const etiquetaMesAnterior = mesAnteriorInfo ? `${getNombreMes(mesAnteriorInfo.iMes)} ${mesAnteriorInfo.iAnio}` : 'Mes Anterior';
    const etiquetaMesAntepasado = mesAntepasadoInfo ? `${getNombreMes(mesAntepasadoInfo.iMes)} ${mesAntepasadoInfo.iAnio}` : 'Mes Antepasado';

    const mesesCards = [
        {
            titulo: 'MES ACTUAL',
            mes: etiquetaMesActual,
            ordenes: resumen.iOrdenesMesActual ?? 0,
            bgColor: [239, 246, 255], // Light blue
            borderColor: [191, 219, 254],
            textColor: [29, 78, 216]
        },
        {
            titulo: 'MES PASADO',
            mes: etiquetaMesAnterior,
            ordenes: resumen.iOrdenesMesAnterior ?? 0,
            bgColor: [254, 243, 199], // Light amber
            borderColor: [253, 230, 138],
            textColor: [180, 83, 9]
        },
        {
            titulo: 'MES ANTEPASADO',
            mes: etiquetaMesAntepasado,
            ordenes: resumen.iOrdenesMesAntepasado ?? 0,
            bgColor: [243, 244, 246], // Light gray
            borderColor: [229, 231, 235],
            textColor: [75, 85, 99]
        }
    ];

    mesesCards.forEach((c, index) => {
        const cardX = margin + (index * (cardOrderWidth + 4));
        doc.setFillColor(c.bgColor[0], c.bgColor[1], c.bgColor[2]);
        doc.setDrawColor(c.borderColor[0], c.borderColor[1], c.borderColor[2]);
        doc.roundedRect(cardX, y, cardOrderWidth, 28, 2, 2, 'FD');

        // Título de la tarjeta
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        doc.setTextColor(colorMuted[0], colorMuted[1], colorMuted[2]);
        doc.text(c.titulo, cardX + (cardOrderWidth / 2), y + 6, { align: 'center' });

        // Nombre del mes
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(colorDark[0], colorDark[1], colorDark[2]);
        doc.text(c.mes, cardX + (cardOrderWidth / 2), y + 11, { align: 'center' });

        // Cantidad de órdenes en GRANDE
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.setTextColor(c.textColor[0], c.textColor[1], c.textColor[2]);
        doc.text(String(c.ordenes), cardX + (cardOrderWidth / 2), y + 21, { align: 'center' });

        // Subtítulo
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(colorMuted[0], colorMuted[1], colorMuted[2]);
        doc.text('Órdenes Pagadas', cardX + (cardOrderWidth / 2), y + 25, { align: 'center' });
    });

    y += 33;

    // Tabla Histórica de los últimos meses (Table 3)
    if (historico.length > 0) {
        doc.setFillColor(colorLightBg[0], colorLightBg[1], colorLightBg[2]);
        doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
        doc.roundedRect(margin, y, contentWidth, 38, 2, 2, 'FD');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(colorDark[0], colorDark[1], colorDark[2]);
        doc.text('HISTÓRICO MENSUAL DE ÓRDENES PAGADAS (ÚLTIMOS 6 MESES)', margin + 5, y + 6);

        // Encabezado de mini-tabla
        const tableY = y + 10;
        doc.setFillColor(241, 245, 249);
        doc.rect(margin + 4, tableY, contentWidth - 8, 5.5, 'F');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        doc.setTextColor(colorMuted[0], colorMuted[1], colorMuted[2]);
        doc.text('Período / Mes', margin + 8, tableY + 4);
        doc.text('Año', margin + 65, tableY + 4);
        doc.text('Cantidad de Órdenes', margin + 110, tableY + 4);
        doc.text('Estatus', margin + 160, tableY + 4);

        let rowY = tableY + 9;
        historico.forEach((item) => {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7.5);
            doc.setTextColor(colorDark[0], colorDark[1], colorDark[2]);

            const mesNombre = `${getNombreMes(item.iMes)} ${item.iAnio}`.toUpperCase();
            doc.text(mesNombre, margin + 8, rowY);
            doc.text(String(item.iAnio || ''), margin + 65, rowY);

            doc.setFont('helvetica', 'bold');
            doc.setTextColor(colorPrimary[0], colorPrimary[1], colorPrimary[2]);
            doc.text(String(item.iCantidadOrdenes ?? 0), margin + 120, rowY);

            doc.setFont('helvetica', 'normal');
            doc.setTextColor(34, 197, 94);
            doc.text('PAGADA', margin + 160, rowY);

            rowY += 4.5;
        });

        y += 42;
    }

    // Totales y Estadísticas generales
    const totalCompras = distribuidor.dMontoVendido !== undefined
        ? parseFloat(distribuidor.dMontoVendido).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : (distribuidor.dTotalVenta !== undefined ? parseFloat(distribuidor.dTotalVenta).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00');

    doc.setFillColor(colorLightBg[0], colorLightBg[1], colorLightBg[2]);
    doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
    doc.roundedRect(margin, y, contentWidth, 18, 2, 2, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(colorDark[0], colorDark[1], colorDark[2]);
    doc.text('TOTAL COMPRAS ACUMULADAS:', margin + 6, y + 11);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(colorGold[0], colorGold[1], colorGold[2]);
    doc.text(`$${totalCompras} MXN`, margin + 60, y + 11);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(colorMuted[0], colorMuted[1], colorMuted[2]);
    doc.text('Documento oficial generado por Akasia Admin Panel.', pageWidth - margin - 6, y + 11, { align: 'right' });

    // Footer institucional
    doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
    doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(colorMuted[0], colorMuted[1], colorMuted[2]);
    doc.text('Akasia Technologies © - Todos los derechos reservados. Confidencial y exclusivo para uso administrativo.', margin, pageHeight - 8);
    doc.text(`Distribuidor #${idCliente}`, pageWidth - margin, pageHeight - 8, { align: 'right' });

    // Descargar el documento
    const filename = `Ficha_Distribuidor_${idCliente}.pdf`;
    doc.save(filename);
};
